import type { DemoData } from '@demo-portal/shared';

export interface TempDemoStorage {
  id: string;
  config: DemoData['config'];
  agents: DemoData['agents'];
  examples: DemoData['examples'];
  createdAt: number;
  expiresAt: number;
}

export class TempDemoManager {
  private static readonly STORAGE_PREFIX = 'temp-demo-';
  private static readonly EXPIRY_HOURS = 1;

  /**
   * Store a temporary demo configuration in session storage
   * @param demoData The demo configuration data to store
   * @returns The temporary demo ID that can be used to retrieve the data
   */
  static store(demoData: DemoData): string {
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substring(2, 8);
    const tempId = `temp-${timestamp}-${randomSuffix}`;
    
    const storageData: TempDemoStorage = {
      id: tempId,
      config: demoData.config,
      agents: demoData.agents,
      examples: demoData.examples,
      createdAt: timestamp,
      expiresAt: timestamp + (this.EXPIRY_HOURS * 60 * 60 * 1000),
    };
    
    try {
      sessionStorage.setItem(
        `${this.STORAGE_PREFIX}${tempId}`, 
        JSON.stringify(storageData)
      );
      
      // Trigger cleanup of expired items when storing new ones
      this.cleanup();
      
      return tempId;
    } catch (error) {
      console.error('Failed to store temporary demo configuration:', error);
      throw new Error('Failed to store temporary demo configuration');
    }
  }

  /**
   * Retrieve a temporary demo configuration from session storage
   * @param tempId The temporary demo ID
   * @returns The demo data if found and not expired, null otherwise
   */
  static retrieve(tempId: string): DemoData | null {
    const key = `${this.STORAGE_PREFIX}${tempId.substring(5)}`;
    
    try {
      const stored = sessionStorage.getItem(key);
      
      if (!stored) {
        return null;
      }
      
      const data: TempDemoStorage = JSON.parse(stored);
      
      // Check if the configuration has expired
      if (Date.now() > data.expiresAt) {
        // Remove expired configuration
        sessionStorage.removeItem(key);
        return null;
      }
      
      return {
        config: data.config,
        agents: data.agents,
        examples: data.examples,
      };
    } catch (error) {
      console.error('Failed to retrieve temporary demo configuration:', error);
      // Remove corrupted data
      sessionStorage.removeItem(key);
      return null;
    }
  }

  /**
   * Check if a demo ID is a temporary demo ID
   * @param demoId The demo ID to check
   * @returns True if the ID follows the temporary demo pattern
   */
  static isTemporaryId(demoId: string): boolean {
    return demoId.startsWith('temp-');
  }

  /**
   * Clean up expired temporary demo configurations from session storage
   */
  static cleanup(): void {
    try {
      const keysToRemove: string[] = [];
      
      // Iterate through all session storage keys
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        
        if (key && key.startsWith(this.STORAGE_PREFIX)) {
          try {
            const stored = sessionStorage.getItem(key);
            if (stored) {
              const data: TempDemoStorage = JSON.parse(stored);
              
              // Mark expired configurations for removal
              if (Date.now() > data.expiresAt) {
                keysToRemove.push(key);
              }
            }
          } catch (error) {
            // Mark corrupted data for removal
            keysToRemove.push(key);
          }
        }
      }
      
      // Remove expired and corrupted configurations
      keysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
      });
      
      if (keysToRemove.length > 0) {
        console.log(`Cleaned up ${keysToRemove.length} expired temporary demo configurations`);
      }
    } catch (error) {
      console.error('Failed to cleanup temporary demo configurations:', error);
    }
  }

  /**
   * Remove a specific temporary demo configuration
   * @param tempId The temporary demo ID to remove
   */
  static remove(tempId: string): void {
    const key = `${this.STORAGE_PREFIX}${tempId}`;
    
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove temporary demo configuration:', error);
    }
  }

  /**
   * Get all temporary demo IDs currently stored (for debugging/testing)
   * @returns Array of temporary demo IDs
   */
  static getAllTempIds(): string[] {
    const tempIds: string[] = [];
    
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        
        if (key && key.startsWith(this.STORAGE_PREFIX)) {
          const tempId = key.replace(this.STORAGE_PREFIX, '');
          tempIds.push(tempId);
        }
      }
    } catch (error) {
      console.error('Failed to get temporary demo IDs:', error);
    }
    
    return tempIds;
  }

  /**
   * Initialize cleanup on page load - should be called once when the app starts
   */
  static initialize(): void {
    // Run initial cleanup
    this.cleanup();
    
    // Set up periodic cleanup every 10 minutes
    setInterval(() => {
      this.cleanup();
    }, 10 * 60 * 1000);
  }
}